import { computeLineTotalForCartItemBase, computeModifiersPerUnit } from "@/domain/cart/cart.pricing";
import { MAX_MENU_ITEM_QUANTITY } from "@/domain/cart/cart.constants";
import type { MenuCatalogItem, MenuCatalogSnapshot } from "@/domain/cart/cart-validity.types";
import type { OrderDetailsItemModifier, OrderDetailsLineItem, OrderDetailsUI } from "@/domain/order/order-details.model";
import { roundMoney } from "@/domain/money/round-money";
import type {
  ReorderDiagnostic,
  ReorderRemovedCustomization,
  ReorderSkippedItem,
  ReorderTransformInput,
  ReorderTransformResult,
} from "@/domain/reorder/reorder.types";
import type { SelectedCartModifier } from "@/models/cart-item";
import type { ModifierGroupItemV2, ModifierGroupV2 } from "@/models/menu-item-v2";
import type { CartLine, CartModifierGroupStored } from "@/shared/cart/model.types";
import { newClientItemId } from "@/shared/ids/client-item-id";

function findCatalogItem(catalog: MenuCatalogSnapshot, menuItemId: string): MenuCatalogItem | undefined {
  const id = String(menuItemId).trim();
  return catalog.items.find((item) => String(item.menu_item_id) === id);
}

function isComboCatalogItem(item: MenuCatalogItem): boolean {
  return (item.components?.length ?? 0) > 0;
}

function canonicalizeModifierGroups(groups: CartModifierGroupStored[]): CartModifierGroupStored[] {
  const raw = groups ?? [];
  if (raw.length === 0) return [];

  const out: CartModifierGroupStored[] = [];
  for (const g of raw) {
    const gid = String(g?.modifier_group_id ?? "").trim();
    if (!gid) continue;
    const items = (g.items ?? [])
      .filter((m) => String(m?.modifier_group_item_id ?? "").trim() !== "")
      .map((m) => ({
        modifier_group_item_id: String(m.modifier_group_item_id).trim(),
        child_menu_item_id: String(m.child_menu_item_id ?? "").trim(),
        quantity: m.quantity,
        delta_price: roundMoney(Number(m.delta_price) || 0),
      }))
      .sort((a, b) => {
        const c = a.modifier_group_item_id.localeCompare(b.modifier_group_item_id);
        if (c !== 0) return c;
        return a.child_menu_item_id.localeCompare(b.child_menu_item_id);
      });
    if (items.length === 0) continue;
    out.push({ modifier_group_id: gid, items });
  }
  out.sort((a, b) => a.modifier_group_id.localeCompare(b.modifier_group_id));
  return out;
}

function rowQuantityForPricing(m: { quantity?: number | null }, parentQty: number): number {
  if (m.quantity === null || m.quantity === undefined) {
    const pq = Math.max(0, Math.floor(Number(parentQty)));
    return pq > 0 ? pq : 1;
  }
  const q = Math.max(0, Math.floor(Number(m.quantity)));
  return q > 0 ? q : 1;
}

function groupsToSelectedModifiers(groups: CartModifierGroupStored[], parentQty: number): SelectedCartModifier[] {
  const out: SelectedCartModifier[] = [];
  for (const g of groups) {
    for (const m of g.items) {
      out.push({
        groupId: g.modifier_group_id,
        itemId: m.modifier_group_item_id,
        quantity: rowQuantityForPricing(m, parentQty),
        delta_price: m.delta_price,
      });
    }
  }
  return out;
}

function finalizeCartLineDraft(line: CartLine): CartLine {
  const qty = Math.max(1, Math.floor(Number(line.quantity) || 0));
  const groups = canonicalizeModifierGroups(line.selected_modifiers);
  const modsForPricing = groupsToSelectedModifiers(groups, qty);
  const addonsTotal = computeModifiersPerUnit(modsForPricing);
  const itemTotal = computeLineTotalForCartItemBase(line.item_price, qty, { selectedModifiers: modsForPricing });
  return {
    ...line,
    quantity: qty,
    selected_modifiers: groups,
    addonsTotal,
    itemTotal,
  };
}

function makeLineDiagnostic(
  code: ReorderDiagnostic["code"],
  message: string,
  line: OrderDetailsLineItem,
  lineIndex: number,
  extra: Partial<ReorderDiagnostic> = {}
): ReorderDiagnostic {
  return {
    code,
    message,
    lineIndex,
    lineName: line.name,
    menuItemId: line.menuItemId.trim() || undefined,
    ...extra,
  };
}

function validateCatalogContext(
  order: OrderDetailsUI,
  catalog: MenuCatalogSnapshot
): ReorderDiagnostic[] {
  const errors: ReorderDiagnostic[] = [];

  if (!catalog.loaded) {
    errors.push({
      code: "CATALOG_NOT_LOADED",
      message: "Menu catalog is not loaded",
      lineIndex: -1,
      lineName: "",
    });
    return errors;
  }

  if (!catalog.restaurantAvailable) {
    errors.push({
      code: "RESTAURANT_UNAVAILABLE",
      message: "Restaurant is not accepting orders",
      lineIndex: -1,
      lineName: "",
    });
    return errors;
  }

  const orderRestaurantId = order.restaurantId.trim();
  const catalogRestaurantId = catalog.restaurantId.trim();
  if (orderRestaurantId && catalogRestaurantId && orderRestaurantId !== catalogRestaurantId) {
    errors.push({
      code: "RESTAURANT_MISMATCH",
      message: "Order restaurant does not match the loaded menu catalog",
      lineIndex: -1,
      lineName: "",
    });
  }

  return errors;
}

/** Catalog preconditions for reorder — exported for pipeline stage (a). */
export function validateReorderCatalog(
  order: OrderDetailsUI,
  catalog: MenuCatalogSnapshot
): ReorderDiagnostic[] {
  return validateCatalogContext(order, catalog);
}

type ResolvedModifierMatch = {
  group: ModifierGroupV2;
  entry: ModifierGroupItemV2;
};

type ModifierResolveFailure = {
  code: Extract<
    ReorderDiagnostic["code"],
    | "MODIFIER_WIRING_MISSING"
    | "MODIFIER_GROUP_NOT_IN_CATALOG"
    | "MODIFIER_NOT_IN_CATALOG"
  >;
  message: string;
  modifierGroupId?: string;
  modifierGroupItemId?: string;
};

/**
 * Resolves a historical modifier to a catalog row.
 * Prefer bridge id; when missing, match uniquely on (group_id, child_menu_item_id)
 * including place-order OR semantics (child may equal bridge). Never guesses ambiguous matches.
 */
function resolveModifierInCatalog(
  catalogItem: MenuCatalogItem,
  modifier: OrderDetailsItemModifier
): ResolvedModifierMatch | ModifierResolveFailure {
  const gid = (modifier.modifierGroupId ?? "").trim();
  const bridge = (modifier.modifierGroupItemId ?? "").trim();
  const child = (modifier.childMenuItemId ?? "").trim();

  const groups = catalogItem.modifier_groups ?? [];

  const matchesChildOrBridge = (entry: ModifierGroupItemV2, needle: string): boolean => {
    if (!needle) return false;
    return (
      String(entry.child_menu_item_id ?? "").trim() === needle ||
      String(entry.modifier_group_item_id ?? "").trim() === needle
    );
  };

  const pickAvailableUnique = (
    group: ModifierGroupV2,
    candidates: ModifierGroupItemV2[]
  ): ResolvedModifierMatch | ModifierResolveFailure => {
    const available = candidates.filter((entry) => entry.is_available !== false);
    if (available.length === 1) {
      return { group, entry: available[0]! };
    }
    if (available.length === 0 && candidates.length > 0) {
      return {
        code: "MODIFIER_NOT_IN_CATALOG",
        message: `modifier "${modifier.name}" is not available on the menu`,
        modifierGroupId: String(group.modifier_group_id),
        modifierGroupItemId: bridge || undefined,
      };
    }
    if (candidates.length > 1) {
      return {
        code: "MODIFIER_WIRING_MISSING",
        message: `modifier "${modifier.name}" matches multiple catalog options`,
        modifierGroupId: String(group.modifier_group_id),
        modifierGroupItemId: bridge || undefined,
      };
    }
    return {
      code: "MODIFIER_NOT_IN_CATALOG",
      message: `modifier "${modifier.name}" is not on the menu`,
      modifierGroupId: String(group.modifier_group_id),
      modifierGroupItemId: bridge || undefined,
    };
  };

  if (gid) {
    const catalogGroup = groups.find((group) => String(group.modifier_group_id) === gid);
    if (!catalogGroup) {
      return {
        code: "MODIFIER_GROUP_NOT_IN_CATALOG",
        message: `modifier group "${gid}" is not on the menu for this item`,
        modifierGroupId: gid,
        modifierGroupItemId: bridge || undefined,
      };
    }

    if (bridge) {
      const byBridge = (catalogGroup.items ?? []).filter(
        (entry) => String(entry.modifier_group_item_id) === bridge
      );
      if (byBridge.length > 0) {
        return pickAvailableUnique(catalogGroup, byBridge);
      }
    }

    if (child) {
      const byChild = (catalogGroup.items ?? []).filter((entry) => matchesChildOrBridge(entry, child));
      if (byChild.length > 0) {
        return pickAvailableUnique(catalogGroup, byChild);
      }
    }

    if (!bridge && !child) {
      return {
        code: "MODIFIER_WIRING_MISSING",
        message: `modifier "${modifier.name}" is missing catalog wiring ids`,
        modifierGroupId: gid,
      };
    }

    return {
      code: "MODIFIER_NOT_IN_CATALOG",
      message: `modifier "${modifier.name}" is not on the menu`,
      modifierGroupId: gid,
      modifierGroupItemId: bridge || undefined,
    };
  }

  // No group id: only resolve when child uniquely identifies one option on the item.
  if (!child && !bridge) {
    return {
      code: "MODIFIER_WIRING_MISSING",
      message: `modifier "${modifier.name}" is missing catalog wiring ids`,
    };
  }

  const needle = child || bridge;
  const cross: Array<{ group: ModifierGroupV2; entry: ModifierGroupItemV2 }> = [];
  for (const group of groups) {
    for (const entry of group.items ?? []) {
      if (matchesChildOrBridge(entry, needle)) {
        cross.push({ group, entry });
      }
    }
  }

  const availableCross = cross.filter((row) => row.entry.is_available !== false);
  if (availableCross.length === 1) {
    return availableCross[0]!;
  }
  if (cross.length > 1 || availableCross.length > 1) {
    return {
      code: "MODIFIER_WIRING_MISSING",
      message: `modifier "${modifier.name}" matches multiple catalog options`,
      modifierGroupItemId: bridge || undefined,
    };
  }
  if (cross.length === 1 && availableCross.length === 0) {
    return {
      code: "MODIFIER_NOT_IN_CATALOG",
      message: `modifier "${modifier.name}" is not available on the menu`,
      modifierGroupId: String(cross[0]!.group.modifier_group_id),
      modifierGroupItemId: bridge || undefined,
    };
  }

  return {
    code: "MODIFIER_NOT_IN_CATALOG",
    message: `modifier "${modifier.name}" is not on the menu`,
    modifierGroupItemId: bridge || undefined,
  };
}

function selectionCountForGroup(
  groups: CartModifierGroupStored[],
  groupId: string
): number {
  const group = groups.find((g) => g.modifier_group_id === groupId);
  if (!group) return 0;
  return (group.items ?? []).reduce((sum, item) => {
    const q = Math.max(0, Math.floor(Number(item.quantity) || 0));
    return sum + (q > 0 ? q : 1);
  }, 0);
}

function buildModifierGroupsFromCatalog(
  line: OrderDetailsLineItem,
  lineIndex: number,
  catalogItem: MenuCatalogItem
): {
  groups: CartModifierGroupStored[];
  warnings: ReorderDiagnostic[];
  removedCustomizations: ReorderRemovedCustomization[];
  needsReview: boolean;
} {
  const warnings: ReorderDiagnostic[] = [];
  const removedCustomizations: ReorderRemovedCustomization[] = [];
  const byGroup = new Map<string, CartModifierGroupStored["items"]>();

  for (const modifier of line.modifiers) {
    const resolved = resolveModifierInCatalog(catalogItem, modifier);
    if ("code" in resolved) {
      warnings.push(
        makeLineDiagnostic(resolved.code, resolved.message, line, lineIndex, {
          modifierName: modifier.name,
          modifierGroupId: resolved.modifierGroupId,
          modifierGroupItemId: resolved.modifierGroupItemId,
        })
      );
      removedCustomizations.push({
        lineIndex,
        lineName: line.name,
        modifierName: modifier.name,
        reasonCode: resolved.code,
      });
      continue;
    }

    const gid = String(resolved.group.modifier_group_id).trim();
    const bridge = String(resolved.entry.modifier_group_item_id).trim();
    const row = {
      modifier_group_item_id: bridge,
      child_menu_item_id: String(
        resolved.entry.child_menu_item_id ?? modifier.childMenuItemId ?? ""
      ).trim(),
      quantity: modifier.quantity,
      delta_price: roundMoney(resolved.entry.delta_price),
    };
    const list = byGroup.get(gid) ?? [];
    list.push(row);
    byGroup.set(gid, list);
  }

  const groups = Array.from(byGroup.entries()).map(([modifier_group_id, items]) => ({
    modifier_group_id,
    items,
  }));

  let needsReview = removedCustomizations.length > 0;

  for (const catalogGroup of catalogItem.modifier_groups ?? []) {
    const minSelect = Math.max(0, Math.floor(Number(catalogGroup.min_select) || 0));
    if (minSelect <= 0) continue;
    const gid = String(catalogGroup.modifier_group_id).trim();
    const selected = selectionCountForGroup(groups, gid);
    if (selected < minSelect) {
      needsReview = true;
      warnings.push(
        makeLineDiagnostic(
          "REQUIRED_MODIFIER_INCOMPLETE",
          `required customizations for "${line.name}" are incomplete`,
          line,
          lineIndex,
          { modifierGroupId: gid }
        )
      );
    }
  }

  return { groups, warnings, removedCustomizations, needsReview };
}

type TransformLineResult = {
  line: CartLine | null;
  skipped: ReorderSkippedItem | null;
  warnings: ReorderDiagnostic[];
  removedCustomizations: ReorderRemovedCustomization[];
};

function transformLine(
  line: OrderDetailsLineItem,
  lineIndex: number,
  order: OrderDetailsUI,
  catalog: MenuCatalogSnapshot
): TransformLineResult {
  const warnings: ReorderDiagnostic[] = [];
  const removedCustomizations: ReorderRemovedCustomization[] = [];

  const menuItemId = line.menuItemId.trim();
  if (!menuItemId) {
    const diagnostic = makeLineDiagnostic(
      "MISSING_MENU_ITEM_ID",
      `Line "${line.name}" is missing a menu item id`,
      line,
      lineIndex
    );
    warnings.push(diagnostic);
    return {
      line: null,
      skipped: {
        lineIndex,
        lineName: line.name,
        reasonCode: "MISSING_MENU_ITEM_ID",
      },
      warnings,
      removedCustomizations,
    };
  }

  if ((line.comboSelections?.length ?? 0) > 0) {
    const diagnostic = makeLineDiagnostic(
      "COMBO_NOT_SUPPORTED",
      `"${line.name}" is a combo meal. Reorder is not available for combos yet.`,
      line,
      lineIndex,
      { menuItemId }
    );
    warnings.push(diagnostic);
    return {
      line: null,
      skipped: {
        lineIndex,
        lineName: line.name,
        menuItemId,
        reasonCode: "COMBO_NOT_SUPPORTED",
      },
      warnings,
      removedCustomizations,
    };
  }

  const catalogItem = findCatalogItem(catalog, menuItemId);
  if (!catalogItem || catalogItem.is_deleted === true) {
    const diagnostic = makeLineDiagnostic(
      "MENU_ITEM_NOT_IN_CATALOG",
      `Item "${line.name}" is not on the menu`,
      line,
      lineIndex,
      { menuItemId }
    );
    warnings.push(diagnostic);
    return {
      line: null,
      skipped: {
        lineIndex,
        lineName: line.name,
        menuItemId,
        reasonCode: "MENU_ITEM_NOT_IN_CATALOG",
      },
      warnings,
      removedCustomizations,
    };
  }

  if (catalogItem.is_available === false) {
    const diagnostic = makeLineDiagnostic(
      "MENU_ITEM_NOT_IN_CATALOG",
      `Item "${line.name}" is currently unavailable`,
      line,
      lineIndex,
      { menuItemId }
    );
    warnings.push(diagnostic);
    return {
      line: null,
      skipped: {
        lineIndex,
        lineName: line.name,
        menuItemId,
        reasonCode: "MENU_ITEM_NOT_IN_CATALOG",
      },
      warnings,
      removedCustomizations,
    };
  }

  if (isComboCatalogItem(catalogItem)) {
    const diagnostic = makeLineDiagnostic(
      "COMBO_NOT_SUPPORTED",
      `"${line.name}" is a combo meal. Reorder is not available for combos yet.`,
      line,
      lineIndex,
      { menuItemId }
    );
    warnings.push(diagnostic);
    return {
      line: null,
      skipped: {
        lineIndex,
        lineName: line.name,
        menuItemId,
        reasonCode: "COMBO_NOT_SUPPORTED",
      },
      warnings,
      removedCustomizations,
    };
  }

  const modifierResult = buildModifierGroupsFromCatalog(line, lineIndex, catalogItem);
  warnings.push(...modifierResult.warnings);
  removedCustomizations.push(...modifierResult.removedCustomizations);

  const restaurantId = order.restaurantId.trim() || catalog.restaurantId.trim() || "unknown";
  const category = line.category.trim() || catalogItem.category.trim() || "general";
  if (!line.category.trim() && category === "general") {
    warnings.push(
      makeLineDiagnostic(
        "CATEGORY_FALLBACK",
        `Category missing for "${line.name}" — defaulted to "${category}"`,
        line,
        lineIndex,
        { menuItemId }
      )
    );
  }

  const draft: CartLine = {
    client_item_id: newClientItemId(),
    menu_item_id: menuItemId,
    quantity: Math.max(1, Math.floor(line.quantity) || 1),
    selected_modifiers: modifierResult.groups,
    item_name: catalogItem.item_name || line.name,
    item_price: roundMoney(catalogItem.item_price),
    category,
    restaurant_id: restaurantId,
    needsReview: modifierResult.needsReview || undefined,
    removedCustomizationNames:
      modifierResult.removedCustomizations.length > 0
        ? modifierResult.removedCustomizations.map((entry) => entry.modifierName)
        : undefined,
  };

  return {
    line: finalizeCartLineDraft(draft),
    skipped: null,
    warnings,
    removedCustomizations,
  };
}

/**
 * Enforces SUM(quantity) GROUP BY menu_item_id <= MAX_MENU_ITEM_QUANTITY across the
 * restored lines — the same aggregate rule the cart store/checkout enforce, applied
 * here because a single historical order can restore multiple lines for the same
 * menu item (different modifier configs). Reduces quantity in restoration order
 * rather than dropping a line outright; a line whose budget is fully exhausted by an
 * earlier line for the same item is skipped instead (mirrors the store's own
 * zero-quantity-removes-the-line rule) rather than kept at quantity 0.
 */
function capMenuItemAggregateQuantity(
  entries: { line: CartLine; lineIndex: number }[]
): { lines: CartLine[]; warnings: ReorderDiagnostic[]; skippedItems: ReorderSkippedItem[] } {
  const warnings: ReorderDiagnostic[] = [];
  const skippedItems: ReorderSkippedItem[] = [];
  const remaining = new Map<string, number>();
  const lines: CartLine[] = [];

  for (const { line, lineIndex } of entries) {
    const menuItemId = String(line.menu_item_id);
    const budget = remaining.has(menuItemId) ? remaining.get(menuItemId)! : MAX_MENU_ITEM_QUANTITY;
    const clamped = Math.max(0, Math.min(line.quantity, budget));
    remaining.set(menuItemId, budget - clamped);

    if (clamped <= 0) {
      warnings.push({
        code: "QUANTITY_REDUCED",
        message: `"${line.item_name}" was not added — the ${MAX_MENU_ITEM_QUANTITY}-per-item limit was already reached by another configuration in this order`,
        lineIndex,
        lineName: line.item_name,
        menuItemId,
      });
      skippedItems.push({ lineIndex, lineName: line.item_name, menuItemId, reasonCode: "QUANTITY_REDUCED" });
      continue;
    }

    if (clamped < line.quantity) {
      warnings.push({
        code: "QUANTITY_REDUCED",
        message: `Quantity for "${line.item_name}" was reduced to ${clamped} to stay within the ${MAX_MENU_ITEM_QUANTITY}-per-item limit`,
        lineIndex,
        lineName: line.item_name,
        menuItemId,
      });
      lines.push({ ...line, quantity: clamped, needsReview: true });
    } else {
      lines.push(line);
    }
  }

  return { lines, warnings, skippedItems };
}

/**
 * Builds store-ready cart lines from order details using the menu catalog as authority.
 * Best-effort: restores available lines/customizations; records skips and removed mods.
 */
export function transformOrderForReorder(input: ReorderTransformInput): ReorderTransformResult {
  const { order, catalog } = input;
  const contextErrors = validateCatalogContext(order, catalog);
  if (contextErrors.length > 0) {
    return {
      lines: [],
      errors: contextErrors,
      warnings: [],
      skippedItems: [],
      removedCustomizations: [],
      requiresReview: false,
    };
  }

  const lineEntries: { line: CartLine; lineIndex: number }[] = [];
  const warnings: ReorderDiagnostic[] = [];
  const skippedItems: ReorderSkippedItem[] = [];
  const removedCustomizations: ReorderRemovedCustomization[] = [];

  order.items.forEach((line, lineIndex) => {
    const result = transformLine(line, lineIndex, order, catalog);
    warnings.push(...result.warnings);
    removedCustomizations.push(...result.removedCustomizations);
    if (result.skipped) {
      skippedItems.push(result.skipped);
    }
    if (result.line) {
      lineEntries.push({ line: result.line, lineIndex });
    }
  });

  const capped = capMenuItemAggregateQuantity(lineEntries);
  warnings.push(...capped.warnings);
  skippedItems.push(...capped.skippedItems);
  const lines = capped.lines;

  const requiresReview =
    removedCustomizations.length > 0 ||
    lines.some((line) => line.needsReview === true) ||
    skippedItems.length > 0;

  return {
    lines,
    errors: [],
    warnings,
    skippedItems,
    removedCustomizations,
    requiresReview,
  };
}
